import { useCallback, useMemo, useState } from 'react';

// Shared filtering hook for the table views.
//
//   rows        — the source list (BEFORE any query/filter)
//   columns     — column defs that may carry `filterValue(row) -> string`.
//                 Columns without `filterValue` are simply not filterable.
//
// Returns:
//   filters       — { [columnKey]: Set<string> } of active selections
//   setFilter     — (columnKey, Set) — replace one column's selection
//   clearFilter   — (columnKey)     — drop one column's filter
//   clearAll      — ()              — drop every active filter
//   activeCount   — number of columns currently filtered
//   valuesByKey   — { [columnKey]: string[] } unique values for each
//                   filterable column, sorted alphabetically with ''
//                   surfaced last so "(empty)" always sits at the bottom
//   applyFilters  — (rows: any[]) => any[] — return rows that pass every
//                   active filter (intersection). When no filters are
//                   active, returns the input unchanged.
export default function useColumnFilters(rows, columns) {
  const [filters, setFilters] = useState({});   // columnKey → Set<string>

  // Pre-compute the unique-values list per filterable column so the
  // dropdown can render without scanning the array on every open.
  // Always computed from the SOURCE rows (not currently-filtered) so the
  // user can see and re-add a value they just filtered out.
  const valuesByKey = useMemo(() => {
    const out = {};
    for (const col of columns) {
      if (typeof col.filterValue !== 'function') continue;
      const seen = new Set();
      for (const row of rows) {
        const raw = col.filterValue(row);
        const v = raw == null ? '' : String(raw);
        seen.add(v);
      }
      const list = [...seen].sort((a, b) => {
        if (a === '' && b !== '') return 1;   // '' sorts last as "(empty)"
        if (b === '' && a !== '') return -1;
        return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
      });
      out[col.key] = list;
    }
    return out;
  }, [rows, columns]);

  const setFilter = useCallback((columnKey, next) => {
    setFilters((prev) => {
      const copy = { ...prev };
      if (!next || next.size === 0) {
        delete copy[columnKey];
      } else {
        copy[columnKey] = next;
      }
      return copy;
    });
  }, []);

  const clearFilter = useCallback((columnKey) => {
    setFilters((prev) => {
      if (!(columnKey in prev)) return prev;
      const copy = { ...prev };
      delete copy[columnKey];
      return copy;
    });
  }, []);

  const clearAll = useCallback(() => setFilters({}), []);

  const applyFilters = useCallback((sourceRows) => {
    const activeKeys = Object.keys(filters);
    if (activeKeys.length === 0) return sourceRows;
    return sourceRows.filter((row) => {
      for (const key of activeKeys) {
        const col = columns.find((c) => c.key === key);
        if (!col || typeof col.filterValue !== 'function') continue;
        const raw = col.filterValue(row);
        const v = raw == null ? '' : String(raw);
        if (!filters[key].has(v)) return false;
      }
      return true;
    });
  }, [filters, columns]);

  return {
    filters,
    setFilter,
    clearFilter,
    clearAll,
    activeCount: Object.keys(filters).length,
    valuesByKey,
    applyFilters
  };
}
